/**
 * Centralized user-facing messages and error mapping for the Animation feature.
 */

export const AnimationErrorMessages = {
  GENERATION_FAILED:
    "An error occurred while generating the animation. Please try again.",
  POLLING_FAILED: "Could not generate animation. Please try again.",
  TIMEOUT:
    "Animation generation timed out after 10 minutes. The animation may still be processing on the server. Please check the Animation Gallery later.",
  STREAM: "Unable to stream video.",
  DOWNLOAD: "Unable to download.",
  LOAD_DETAIL: (detail) => `Failed to load animation: ${detail}`,
  CLIPBOARD_URL: "Failed to copy animation URL.",
  COPY_PROMPT_FAILED: "Failed to copy prompt: ",
  REVISED_REQUIRED: "Revised prompt is required for regeneration.",
  REGENERATE_NO_PRIOR: "Please generate an animation first.",
  VIDEO_NOT_READY: "Video is not ready yet.",
};

/**
 * @param {unknown} error
 * @param {string} [fallback]
 * @returns {string}
 */
export function getUserFacingMessage(error, fallback) {
  if (error == null) {
    return fallback;
  }
  if (typeof error === "string") {
    return error;
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return fallback;
}

/**
 * @param {string} context
 * @param {unknown} error
 */
export function logAnimationError(context, error) {
  console.error(`[Animation:${context}]`, error);
}

/**
 * @param {string} context
 * @param {unknown} error
 */
export function logAnimationWarning(context, error) {
  console.warn(`[Animation:${context}]`, error);
}

/**
 * Runs an async function and maps failures to a user string via setErrorMessage.
 * @param {(msg: string) => void} setErrorMessage
 * @param {() => Promise<void>} asyncFn
 * @param {string} fallbackMessage
 * @param {string} [logContext]
 */
export async function runWithAnimationError(
  setErrorMessage,
  asyncFn,
  fallbackMessage,
  logContext = "action"
) {
  try {
    await asyncFn();
  } catch (error) {
    logAnimationError(logContext, error);
    setErrorMessage(getUserFacingMessage(error, fallbackMessage));
  }
}
